import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { extractFilename, fetchAuthenticatedImage } from 'utils/imageUtils'

/**
 * Component that displays an image fetched with authentication headers
 * This is needed because <img> tags cannot send custom headers
 */
const AuthenticatedImage = ({ src, alt, className, platform = 'admin', accessToken, ...otherProps }) => {
  const [blobUrl, setBlobUrl] = useState(null)
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // If no src, don't fetch
    if (!src) {
      setLoading(false)
      return
    }

    // Extract filename from S3 URL or use as-is if it's already a filename
    const filename = extractFilename(src)

    if (!filename) {
      setLoading(false)
      return
    }

    // Get access token from props or localStorage
    const token = accessToken || (() => {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}')
        return user.token || user.access_token
      } catch {
        return null
      }
    })()

    if (!token) {
      console.warn('No access token available for authenticated image')
      setLoading(false)
      setError(true)
      return
    }

    let cancelled = false
    setLoading(true)
    setError(false)

    fetchAuthenticatedImage(filename, platform, token)
      .then(url => {
        if (cancelled) return
        if (url) {
          setBlobUrl(url)
          setError(false)
        } else {
          setError(true)
        }
        setLoading(false)
      })
      .catch(err => {
        console.error('Error loading authenticated image:', err)
        if (cancelled) return
        setError(true)
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [src, platform, accessToken])

  if (loading) {
    return <div className={`authenticated-image-loading ${className || ''}`}>Loading...</div>
  }

  if (error || !blobUrl) {
    return (
      <div className={`authenticated-image-error ${className || ''}`} {...otherProps}>
        <i className='far fa-image' />
      </div>
    )
  }

  return (
    <img
      src={blobUrl}
      alt={alt || ''}
      className={className}
      {...otherProps}
    />
  )
}

AuthenticatedImage.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  className: PropTypes.string,
  platform: PropTypes.string,
  accessToken: PropTypes.string,
}

export default AuthenticatedImage
